import {
  useGlobalNotifier,
  useMessage,
  useSpreadsheetState,
} from "@vrf/fr-vrf-core-react";

import { FrVrfSpreadsheet } from "@vrf/fr-vrf-spreadsheet-react";
import {
  FrVrfAccordion,
  FrVrfButton,
  FrVrfButtonPanel,
  FrVrfCheckboxField,
  FrVrfCheckboxesField,
  FrVrfDropdownField,
  FrVrfFooter,
  FrVrfFormPanel,
  FrVrfMultipleLineInputField,
  FrVrfPanel,
  FrVrfSingleLineInputField,
} from "@vrf/fr-vrf-web-components-react";
import { stringify } from "../../util/querystring-util";
import React, { useEffect, useMemo } from "react";
import { createPath, useLocation, useNavigate } from "react-router-dom";
import {
  RootCpntIchr,
  RootCpntIchrKeys,
  RootCpntIchrSpreadsheetRow,
  SpreadsheetState,
} from ".";
import { useAppState } from "../../app";
import { ApplicationMessageScreen } from "../../messages/screen-message";
import {
  CreateComponentRequest,
  useCreateComponent,
} from "../../services/component";
import {
  useGetDipByRegionList,
  useGetEdhNotificationTypeList,
  useGetEdhProtocolCombinationList,
  useGetEdhProtocolList,
  useGetGeneralCodes,
} from "../../services/data-list";
import {
  CreateRootComponentRequest,
  RootCpntIchr as UpdateRootCpntIchr,
  useCreateRootComponent,
} from "../../services/root-component";
import { useGetRootComponents } from "../../services/root-components";
import { mappingErrorResponseToSpreadsheet } from "../../util";
import {
  CreateDynamicEdhProtocol,
  createDynamicEdhProtocol,
} from "../../util/edh-component-logic";
import {
  InDefCommonFlagType,
  IoType,
} from "../../util/general-code/enums/general-codes";
import { Option } from "../../util/option";
import { usePageNavigation } from "../use-page-navigation";
import {
  ComponentAdditionPageMode,
  REQUEST_CD_TYPES,
  TEMPLATE_FILE_URL,
  isChecked,
  requestCdTypes,
} from "./ComponentAdditionPageConstant";
import { usePageState } from "./usePageState";
import _ from "lodash";
import { Cell } from "@vrf/fr-vrf-spreadsheet-core-templating";

export const ComponentAdditionPageContent = () => {
  const [appState, setAppState] = useAppState();
  const [pageState, setPageState] = usePageState();
  const { messageForScreen } = useMessage();
  const { searchResult, formEditData, dataList } = pageState;

  const [getGeneralCodesResponse, sendGetGeneralCodesRequest] =
    useGetGeneralCodes();
  const [getEdhProtocolListResponse, sendGetEdhProtocolListRequest] =
    useGetEdhProtocolList();
  const [
    getInboundEdhNotificationTypeListResponse,
    sendGetInboundEdhNotificationTypeListRequest,
  ] = useGetEdhNotificationTypeList();
  const [
    getOutboundEdhNotificationTypeListResponse,
    sendGetOutboundEdhNotificationTypeListRequest,
  ] = useGetEdhNotificationTypeList();
  const [getDipByRegionListResponse, sendGetDipByRegionListRequest] =
    useGetDipByRegionList();
  const [getCreateRootComponentResponse, sendCreateRootComponentRequest] =
    useCreateRootComponent();
  const [getCreateComponentResponse, sendCreateComponentRequest] =
    useCreateComponent();
  const [getRootComponentsResponse, sendGetRootComponentsRequest] =
    useGetRootComponents();
  const [getEdhProtocolCombinationResponse, sendEdhProtocolCombinationRequest] =
    useGetEdhProtocolCombinationList();

  const navigate = useNavigate();

  const { showConfirmationDialog, errorMessages, resetUserError } =
    useGlobalNotifier();
  const { isModalPage } = usePageNavigation();

  const search = useLocation().search;
  const queryParameter = new URLSearchParams(search);
  const params = {
    mode: queryParameter.get("mode") ?? "",
    godFlg: queryParameter.get("godFlg") ?? InDefCommonFlagType.NO,
    rootCpntId: queryParameter.get("rootCpntId") ?? "",
    dipByRgn: queryParameter.get("dipByRgn") ?? "",
  };

  const isGodMode: boolean = params.godFlg === InDefCommonFlagType.YES;

  const onInitialDisplayed = () => {
    resetUserError();
    setAppState({
      ...appState,
      title: "Component Addition",
      isGodMode,
    });

    sendGetGeneralCodesRequest({ cdTypes: requestCdTypes });
    sendGetEdhProtocolListRequest({ godOnlyFlag: params.godFlg });
    sendGetDipByRegionListRequest({});
    sendGetRootComponentsRequest({});
    sendEdhProtocolCombinationRequest({});
  };

  const onChangeParams = () => {
    if (!params.dipByRgn || !params.rootCpntId) return;
    sendGetInboundEdhNotificationTypeListRequest({
      inOutTypeCd: IoType.INBOUND,
      dipByRgn: params.dipByRgn,
    });
    sendGetOutboundEdhNotificationTypeListRequest({
      inOutTypeCd: IoType.OUTBOUND,
      dipByRgn: params.dipByRgn,
    });
    setPageState((prevState) => ({
      ...prevState,
      searchResult: {
        ...prevState.searchResult,
        dipByRgn: params.dipByRgn,
        rootCpntId: params.rootCpntId,
      },
    }));
  };

  const onGetGeneralCodesRequest = () => {
    if (!getGeneralCodesResponse) return;
    const cpntOwnType =
      getGeneralCodesResponse.gnrlCdTypeList.find(
        (e) => e.cdType === REQUEST_CD_TYPES.CPNT_OWN_TYPE,
      )?.cdList ?? [];

    const cpntAlctnType =
      getGeneralCodesResponse.gnrlCdTypeList.find(
        (e) => e.cdType === REQUEST_CD_TYPES.CPNT_ALCTN_TYPE,
      )?.cdList ?? [];

    const clodSrvc =
      getGeneralCodesResponse.gnrlCdTypeList.find(
        (e) => e.cdType === REQUEST_CD_TYPES.CLOD_SRVC,
      )?.cdList ?? [];

    const clodSrvcRgn =
      getGeneralCodesResponse.gnrlCdTypeList.find(
        (e) => e.cdType === REQUEST_CD_TYPES.CLOD_SRVC_RGN,
      )?.cdList ?? [];

    const startMethod =
      getGeneralCodesResponse.gnrlCdTypeList.find(
        (e) => e.cdType === REQUEST_CD_TYPES.INTERFACE_START_METHOD_TYPE,
      )?.cdList ?? [];

    setPageState((prevState) => ({
      ...prevState,
      dataList: {
        ...prevState.dataList,
        cpntOwnType,
        cpntAlctnType,
        clodSrvc,
        clodSrvcRgn,
        startMethod,
      },
      searchResult: {
        ...prevState.searchResult,
        dipByRgn: params.dipByRgn,
        rootCpntId: params.rootCpntId,
      },
    }));
  };

  const onGetInboundEdhNotificationTypeListResponse = () => {
    if (!getInboundEdhNotificationTypeListResponse) return;
    setPageState((prevState) => ({
      ...prevState,
      formEditData: {
        ...prevState.formEditData,
        inboundNotificatorList: [],
      },
      dataList: {
        ...prevState.dataList,
        inboundNotificator:
          getInboundEdhNotificationTypeListResponse.edhNotificationTypeList.map(
            (e) => ({
              label: e.edhNtfctnTypeName,
              value: e.edhNtfctnTypeCd,
            }),
          ),
      },
    }));
  };

  const onGetOutboundEdhNotificationTypeListResponse = () => {
    if (!getOutboundEdhNotificationTypeListResponse) return;
    setPageState((prevState) => ({
      ...prevState,
      formEditData: {
        ...prevState.formEditData,
        outboundNotificatorList: [],
      },
      dataList: {
        ...prevState.dataList,
        outboundNotificator:
          getOutboundEdhNotificationTypeListResponse.edhNotificationTypeList.map(
            (e) => ({
              label: e.edhNtfctnTypeName,
              value: e.edhNtfctnTypeCd,
            }),
          ),
      },
    }));
  };

  const onGetDipByRegionListResponse = () => {
    if (!getDipByRegionListResponse) return;
    setPageState((prevState) => ({
      ...prevState,
      dataList: {
        ...prevState.dataList,
        dipByRgnList: getDipByRegionListResponse.dipByRgnList.map((e) => ({
          label: e.dipByRgn,
          value: e.dipByRgn,
        })),
      },
    }));
  };

  const onGetRootComponentsResponse = () => {
    if (!getRootComponentsResponse) return;
    const rootCpntList: Option[] = getRootComponentsResponse.rootCpntList
      .filter(
        (e) =>
          e.dipByRgn === searchResult.dipByRgn &&
          searchResult.dipByRgn.length > 0 &&
          e.delFlg === InDefCommonFlagType.NO,
      )
      .map((rootCpnt) => {
        return {
          label: rootCpnt.rootCpntId,
          value: rootCpnt.rootCpntId,
        };
      });

    setPageState((prevState) => ({
      ...prevState,
      dataList: {
        ...prevState.dataList,
        rootComponent: rootCpntList,
      },
    }));
  };

  const onGetCreateRootComponentResponse = () => {
    if (getCreateRootComponentResponse == undefined) return;

    if (Array.isArray(getCreateRootComponentResponse)) {
      const currentSpreadsheetRecordData: RootCpntIchrSpreadsheetRow[] =
        spreadsheetState ? [...spreadsheetState.rootCpntIchr] : [];

      const newSpreadsheetRecordData = mappingErrorResponseToSpreadsheet(
        getCreateRootComponentResponse,
        currentSpreadsheetRecordData,
        pageState.displayColumn,
      );

      setSpreadsheetState({
        ...spreadsheetState,
        rootCpntIchr: newSpreadsheetRecordData,
      });
    } else {
      setAppState({ ...appState, isShowSuccessToast: true });
      navigate(-1);
    }
  };

  const onGetCreateComponentResponse = () => {
    if (getCreateComponentResponse == undefined) return;
    setAppState({ ...appState, isShowSuccessToast: true });
    const previousPage = isGodMode ? -2 : -1;
    navigate(previousPage);
  };

  const onGetEdhProtocolFlagUsageListResponse = () => {
    if (!getEdhProtocolCombinationResponse || !getEdhProtocolListResponse)
      return;
    const edhProtcolList = getEdhProtocolListResponse.edhProtocolList
      .filter((e) => isGodMode || e.godOnlyFlag === InDefCommonFlagType.NO)
      .map((e) => ({
        label: e.edhPrtclDsplyName,
        value: e.inOutTypeCd + "/" + e.edhPrtclTypeCd,
      }));
    setPageState((prevState) => ({
      ...prevState,
      edhProtocolCombination:
        getEdhProtocolCombinationResponse.edhPrtclCmbntnDfnList,
      dataList: {
        ...prevState.dataList,
        edhProtcolList,
      },
      searchResult: {
        ...prevState.searchResult,
        dipByRgn: params.dipByRgn,
        rootCpntId: params.rootCpntId,
      },
    }));
  };

  const onUpdateDipByRgnSelected = () => {
    if (
      !getRootComponentsResponse ||
      getRootComponentsResponse.rootCpntList.length === 0
    ) {
      return;
    }
    const newRootCpntList: Option[] = getRootComponentsResponse.rootCpntList
      .filter(
        (e) =>
          e.dipByRgn === searchResult.dipByRgn &&
          e.delFlg === InDefCommonFlagType.NO &&
          searchResult.dipByRgn.length > 0,
      )
      .map((rootCpnt) => {
        return {
          label: rootCpnt.rootCpntName,
          value: rootCpnt.rootCpntId,
        };
      });

    if (params.mode === ComponentAdditionPageMode.ADD_COMPONENT) {
      setPageState((prevState) => ({
        ...prevState,
        dataList: {
          ...prevState.dataList,
          rootComponent: newRootCpntList,
        },
        searchResult: {
          ...prevState.searchResult,
          rootCpntId: "",
        },
      }));
    }
  };

  const onUpdateDynamicCheckboxes = () => {
    const createDynamicEdhProtocolProps: CreateDynamicEdhProtocol = {
      edhProtocolCombinationList: pageState.edhProtocolCombination,
      edhProtocolList: dataList.edhProtcolList,
      edhProtocolCheckList: formEditData.edhPrtclList,
    };

    const { dynamicEdhProtocolList, edhProtocolCheckList } =
      createDynamicEdhProtocol(createDynamicEdhProtocolProps);

    const isChangeValue =
      JSON.stringify(formEditData.edhPrtclList) !==
      JSON.stringify(edhProtocolCheckList);

    setPageState((prevState) => ({
      ...prevState,
      formEditData: {
        ...prevState.formEditData,
        edhPrtclList: isChangeValue
          ? edhProtocolCheckList
          : prevState.formEditData.edhPrtclList,
      },
      dataList: {
        ...dataList,
        dynamicEdhProtocolList,
      },
    }));
  };

  const transition = (path: string) => {
    const href = createPath({
      pathname: `${path}`,
    });
    navigate(href);
  };

  useEffect(onInitialDisplayed, [
    params.dipByRgn,
    params.godFlg,
    params.mode,
    params.rootCpntId,
  ]);
  useEffect(onChangeParams, [params.dipByRgn, params.rootCpntId]);
  useEffect(onGetGeneralCodesRequest, [getGeneralCodesResponse]);
  useEffect(onGetInboundEdhNotificationTypeListResponse, [
    getInboundEdhNotificationTypeListResponse,
  ]);
  useEffect(onGetOutboundEdhNotificationTypeListResponse, [
    getOutboundEdhNotificationTypeListResponse,
  ]);
  useEffect(onGetDipByRegionListResponse, [getDipByRegionListResponse]);
  useEffect(onGetRootComponentsResponse, [getRootComponentsResponse]);
  useEffect(onGetCreateRootComponentResponse, [getCreateRootComponentResponse]);
  useEffect(onGetCreateComponentResponse, [getCreateComponentResponse]);
  useEffect(onGetEdhProtocolFlagUsageListResponse, [
    getEdhProtocolCombinationResponse,
    getEdhProtocolListResponse,
  ]);
  useEffect(onUpdateDipByRgnSelected, [searchResult.dipByRgn]);
  useEffect(onUpdateDynamicCheckboxes, [
    pageState.edhProtocolCombination,
    formEditData.edhPrtclList,
  ]);

  const onChangeValueHandle = (name: string) => (event: CustomEvent) => {
    const value = event.detail.value;
    let isValid = event.detail.isValid;
    if (name === "dipByRgn") {
      resetUserError();
      sendGetInboundEdhNotificationTypeListRequest({
        inOutTypeCd: IoType.INBOUND,
        dipByRgn: value,
      });
      sendGetOutboundEdhNotificationTypeListRequest({
        inOutTypeCd: IoType.OUTBOUND,
        dipByRgn: value,
      });
    }
    setPageState((prevState) => ({
      ...prevState,
      searchResult: {
        ...prevState.searchResult,
        [name]: value,
      },
      formValidation: {
        ...prevState.formValidation,
        [name]: isValid,
      },
      isEdited: true,
    }));
  };

  /**
   * GOD
   */
  const onClickGodConfirmDialogHandle = () => {
    showConfirmationDialog({
      title: "",
      message: messageForScreen(ApplicationMessageScreen.APP_SCN_00046),
      type: "warning",
      onOkButtonClicked: () => {
        resetUserError();
        const requestParam = {
          ...params,
          godFlg: InDefCommonFlagType.YES as string,
        };
        transition(`/component-addition?${stringify(requestParam)}`);
      },
    });
  };

  /**
   * Root Component
   */
  const [spreadsheetState, setSpreadsheetState, initSpreadsheetState] =
    useSpreadsheetState<SpreadsheetState>();

  const spreadsheetInitialData = useMemo<SpreadsheetState>(() => {
    const rootCpntIchr = searchResult.rootCpntIchr.map((e) => ({
      ...e,
      messageId: "",
      message: "",
      column: "",
      errorColumnList: [],
      errorMessage: "",
      messageList: [],
    }));
    return {
      rootCpntIchr: rootCpntIchr,
    };
  }, [searchResult.rootCpntIchr]);

  const onChnageCheckHandle = (name: string) => (event: CustomEvent) => {
    const value = event.detail.value;
    const isValid = event.detail.isValid;
    setPageState({
      ...pageState,
      searchResult: {
        ...searchResult,
        [name]: value ? InDefCommonFlagType.YES : InDefCommonFlagType.NO,
      },
      formValidation: {
        ...pageState.formValidation,
        [name]: isValid,
      },
      isEdited: true,
    });
  };

  const onAddRowClicked = () => {
    if (!spreadsheetState) return;
    const insertRowIndex: number =
      (pageState.spreadsheetSelectedRowIndex ?? 0) + 1;

    const newSpreadsheetState: RootCpntIchrSpreadsheetRow[] = spreadsheetState
      ? [...spreadsheetState.rootCpntIchr]
      : [];
    const addSpreadsheetRow: RootCpntIchrSpreadsheetRow = {
      isChanged: InDefCommonFlagType.YES,
      ichrId: "",
      ichrName: "",
      pstnName: "",
      cmpnyName: "",
      telNum: "",
      mlAdrs: "",
      updateDateTimeMRootCpntIchr: "",
      messageId: "",
      message: "",
      column: "",
      errorColumnList: [],
      errorMessage: "",
      messageList: [],
    };
    newSpreadsheetState.splice(insertRowIndex, 0, addSpreadsheetRow);

    setSpreadsheetState({
      ...spreadsheetState,
      rootCpntIchr: [...newSpreadsheetState],
    });
    setPageState((prevState) => ({
      ...prevState,
      isEdited: true,
    }));
  };
  /**
   * Spreadsheet cell selected.
   * @param event that is passed from spreadsheet.
   */
  const onCellSelected = (event: CustomEvent) => {
    const rowIndex: number = event.detail.cell?.data.index;
    setPageState((prevState) => ({
      ...prevState,
      spreadsheetSelectedRowIndex: rowIndex,
    }));
  };

  /**
   * Spreadsheet delete icon clicked.
   * @param event that is passed from spreadsheet.
   */
  const onDeleteIconClicked = (event: CustomEvent) => {
    if (event.detail.name !== "deleteIcon") return;
    const rowIndex: number = event.detail.cell.data.index;
    const newSpreadsheetState: RootCpntIchrSpreadsheetRow[] = spreadsheetState
      ? [...spreadsheetState.rootCpntIchr]
      : [];

    newSpreadsheetState.splice(rowIndex, 1);
    setSpreadsheetState({
      ...spreadsheetState,
      rootCpntIchr: newSpreadsheetState,
    });
    setPageState((prevState) => ({
      ...prevState,
      isEdited: true,
    }));
  };

  const isRootCpntIchrKey = (arg: any): arg is keyof RootCpntIchr => {
    const dtKindKeys: (keyof RootCpntIchr)[] = [
      "ichrName",
      "pstnName",
      "cmpnyName",
      "telNum",
      "mlAdrs",
    ];
    return dtKindKeys.includes(arg);
  };

  const onEditSpreadsheet = (event: CustomEvent) => {
    if (!spreadsheetState) return;
    const eventName: string = event.detail.name;
    const rowIndex: number = event.detail.cell.data.index;
    const value = event.detail.cell.value;

    if (isRootCpntIchrKey(eventName)) {
      const copyRootCpntIchrList: RootCpntIchrSpreadsheetRow[] = JSON.parse(
        JSON.stringify(spreadsheetState.rootCpntIchr),
      );

      copyRootCpntIchrList[rowIndex][eventName] = value;
      copyRootCpntIchrList[rowIndex].isChanged = InDefCommonFlagType.YES;
      const newState = {
        ...spreadsheetState,
        rootCpntIchr: copyRootCpntIchrList,
      };
      setSpreadsheetState(newState);
      setPageState((prevState) => ({
        ...prevState,
        isEdited: true,
      }));
    }
  };

  const onCellBulkEdited = (event: CustomEvent) => {
    if (!spreadsheetState) return;
    const newPicAddressList = _.cloneDeep(
      spreadsheetState?.rootCpntIchr,
    ) as RootCpntIchrSpreadsheetRow[];

    const colName = event.detail.name as RootCpntIchrKeys;

    event.detail.affectedCells.cells.forEach((item: Cell) => {
      const value: string = item.value;
      const rowIdx: number = item.data.index;
      newPicAddressList[rowIdx][colName] = value;
    });

    setPageState({
      ...pageState,
      isEdited: true,
    });

    setSpreadsheetState({
      ...spreadsheetState,
      rootCpntIchr: newPicAddressList,
    });
  };

  /**
   * Component
   */

  const onChnageCheckboxHandle = (name: string) => (event: CustomEvent) => {
    const value = event.detail.value;
    setPageState((prevState) => ({
      ...prevState,
      searchResult: {
        ...prevState.searchResult,
        [name]: value ? InDefCommonFlagType.YES : InDefCommonFlagType.NO,
      },
      isEdited: true,
    }));
  };
  const onChnageCheckboxesHandle = (name: string) => (event: CustomEvent) => {
    const value: string[] = event.detail.value;
    setPageState((prevState) => ({
      ...prevState,
      formEditData: {
        ...prevState.formEditData,
        [name]: value,
      },
      isEdited: true,
    }));
  };

  /**
   * Footer
   */
  const onClickSaveButton = () => {
    showConfirmationDialog({
      type: "confirm",
      title: "",
      message: messageForScreen(ApplicationMessageScreen.APP_SCN_00045),
      onOkButtonClicked: () => {
        resetUserError();
        const edhPrtclList: CreateComponentRequest["edhPrtclList"] =
          formEditData.edhPrtclList.map((e) => {
            const value = e.split("/");
            return { inOutTypeCd: value[0], edhPrtclTypeCd: value[1] };
          });
        const ifStrtMthdList: CreateComponentRequest["ifStrtMthdList"] =
          formEditData.ifStrtMthdList.map((e) => ({ ifStrtMthdTypeCd: e }));
        const inboundEdhNtfctnTypeList =
          formEditData.inboundNotificatorList.map((e) => ({
            inOutTypeCd: IoType.INBOUND,
            edhNtfctnTypeCd: e,
          }));
        const outboundEdhNtfctnTypeList =
          formEditData.outboundNotificatorList.map((e) => ({
            inOutTypeCd: IoType.OUTBOUND,
            edhNtfctnTypeCd: e,
          }));
        const edhNtfctnTypeList: CreateComponentRequest["edhNtfctnTypeList"] = [
          ...inboundEdhNtfctnTypeList,
          ...outboundEdhNtfctnTypeList,
        ];

        if (params.mode === ComponentAdditionPageMode.ADD_ROOT_COMPONENT) {
          const rootCpntIchrList = [] as UpdateRootCpntIchr[];
          spreadsheetState?.rootCpntIchr.map((e: RootCpntIchr, idx) => {
            const rootCpntIchr = {} as UpdateRootCpntIchr;
            rootCpntIchr.index = idx;
            rootCpntIchr.ichrId = e.ichrId;
            rootCpntIchr.ichrName = e.ichrName;
            rootCpntIchr.pstnName = e.pstnName;
            rootCpntIchr.cmpnyName = e.cmpnyName;
            rootCpntIchr.telNum = e.telNum;
            rootCpntIchr.mlAdrs = e.mlAdrs;
            rootCpntIchrList.push(rootCpntIchr);
          });
          const createRootCmp: CreateRootComponentRequest = {
            godFlg: params.godFlg,
            dipByRgn: pageState.searchResult.dipByRgn,
            rootCpntId: pageState.searchResult.rootCpntId,
            rootCpntName: pageState.searchResult.rootCpntName,
            ownTypeCd: pageState.searchResult.ownTypeCd,
            systemName: pageState.searchResult.systemName,
            systemId: pageState.searchResult.systemId,
            subSystemId: pageState.searchResult.subSystemId,
            alctnTypeCd: pageState.searchResult.alctnTypeCd,
            clodSrvcTypeCd: pageState.searchResult.clodSrvcTypeCd,
            rgnTypeCd: pageState.searchResult.rgnTypeCd,
            acntName: pageState.searchResult.acntName,
            notSysUsg:
              pageState.searchResult.notSystemUsg !== ""
                ? pageState.searchResult.notSystemUsg
                : InDefCommonFlagType.NO,
            rootCpntIchrList: rootCpntIchrList,
            cpntId: pageState.searchResult.cpntId,
            cpntNameWtngChng: pageState.searchResult.cpntName,
            cpntDesc: pageState.searchResult.cpntDesc,
            gpgEncryptnFlg:
              pageState.searchResult.gpgEncryptnFlg !== ""
                ? pageState.searchResult.gpgEncryptnFlg
                : InDefCommonFlagType.NO,
            useAssmdRoleFlg:
              pageState.searchResult.useAssmdRoleFlg !== ""
                ? pageState.searchResult.useAssmdRoleFlg
                : InDefCommonFlagType.NO,
            ediUsgFlg:
              pageState.searchResult.ediUsgFlg !== ""
                ? pageState.searchResult.ediUsgFlg
                : InDefCommonFlagType.NO,
            useEdhCnvtrFlg:
              pageState.searchResult.useEdhCnvtrFlg !== ""
                ? pageState.searchResult.useEdhCnvtrFlg
                : InDefCommonFlagType.NO,
            hubFlg:
              pageState.searchResult.hubFlg !== ""
                ? pageState.searchResult.hubFlg
                : InDefCommonFlagType.NO,
            edhPrtclList: edhPrtclList,
            ifStrtMthdList: ifStrtMthdList,
            edhNtfctnTypeList: edhNtfctnTypeList,
          };
          sendCreateRootComponentRequest(createRootCmp);
          return;
        }
        const createCmp: CreateComponentRequest = {
          godFlg: params.godFlg,
          dipByRgn: pageState.searchResult.dipByRgn,
          cpntId: pageState.searchResult.cpntId,
          cpntName: pageState.searchResult.cpntName,
          cpntDesc: pageState.searchResult.cpntDesc,
          rootCpntId: pageState.searchResult.rootCpntId,
          gpgEncryptnFlg:
            pageState.searchResult.gpgEncryptnFlg !== ""
              ? pageState.searchResult.gpgEncryptnFlg
              : InDefCommonFlagType.NO,
          useAssmdRoleFlg:
            pageState.searchResult.useAssmdRoleFlg !== ""
              ? pageState.searchResult.useAssmdRoleFlg
              : InDefCommonFlagType.NO,
          ediUsgFlg:
            pageState.searchResult.ediUsgFlg !== ""
              ? pageState.searchResult.ediUsgFlg
              : InDefCommonFlagType.NO,
          useEdhCnvtrFlg:
            pageState.searchResult.useEdhCnvtrFlg !== ""
              ? pageState.searchResult.useEdhCnvtrFlg
              : InDefCommonFlagType.NO,
          hubFlg:
            pageState.searchResult.hubFlg !== ""
              ? pageState.searchResult.hubFlg
              : InDefCommonFlagType.NO,
          edhPrtclList: edhPrtclList,
          ifStrtMthdList: ifStrtMthdList,
          edhNtfctnTypeList: edhNtfctnTypeList,
        };
        sendCreateComponentRequest(createCmp);
      },
    });
  };

  return (
    <>
      {/* GOD */}
      {params.mode === ComponentAdditionPageMode.ADD_COMPONENT &&
        params.godFlg !== InDefCommonFlagType.YES && (
          <>
            <FrVrfButtonPanel>
              <FrVrfButton
                content="IE Maintenance"
                size="medium"
                theme="caution"
                onClicked={onClickGodConfirmDialogHandle}
                authActId="component_addition.button.god"
              />
            </FrVrfButtonPanel>
            <br />
          </>
        )}
      {/* Root Component */}
      {params.mode === ComponentAdditionPageMode.ADD_ROOT_COMPONENT && (
        <FrVrfAccordion summary="Root component">
          <FrVrfPanel>
            <FrVrfFormPanel columns={2}>
              <FrVrfFormPanel>
                <FrVrfSingleLineInputField
                  labelContent="Root Component ID"
                  content={searchResult.rootCpntId}
                  onInputValueChanged={onChangeValueHandle("rootCpntId")}
                  required
                  errorMessage={errorMessages(
                    pageState.fieldPath.rootCpntId,
                    isModalPage(),
                  )}
                  constraint="[Size(value, {min:0 , max: 64}), IsNotEmpty(value), Pattern(value,{regex:'^[0-9A-Z_]+$',name:'Root Component ID'})]"
                />
                <FrVrfSingleLineInputField
                  labelContent="Root Component Name"
                  content={searchResult.rootCpntName}
                  onInputValueChanged={onChangeValueHandle("rootCpntName")}
                  required
                  errorMessage={errorMessages(
                    pageState.fieldPath.rootCpntName,
                    isModalPage(),
                  )}
                  constraint="[Size(value, {min: 0, max: 256}), IsNotEmpty(value)]"
                />
                <FrVrfDropdownField
                  autocomplete={false}
                  labelContent="DIP by Region"
                  selectedValue={searchResult.dipByRgn}
                  includeDefaultValue={false}
                  items={dataList.dipByRgnList}
                  onValueChanged={onChangeValueHandle("dipByRgn")}
                  required
                  errorMessage={errorMessages(
                    pageState.fieldPath.dipByRgn,
                    isModalPage(),
                  )}
                />
                <FrVrfDropdownField
                  autocomplete={false}
                  labelContent="Owner Type"
                  selectedValue={searchResult.ownTypeCd}
                  includeDefaultValue={false}
                  items={dataList.cpntOwnType}
                  onValueChanged={onChangeValueHandle("ownTypeCd")}
                  errorMessage={errorMessages(
                    pageState.fieldPath.ownTypeCd,
                    isModalPage(),
                  )}
                />
                <FrVrfSingleLineInputField
                  labelContent="System Name"
                  content={searchResult.systemName}
                  onInputValueChanged={onChangeValueHandle("systemName")}
                  errorMessage={errorMessages(
                    pageState.fieldPath.systemName,
                    isModalPage(),
                  )}
                  constraint="Size(value, {min: 0, max: 256})"
                />
                <FrVrfSingleLineInputField
                  labelContent="System ID"
                  content={searchResult.systemId}
                  onInputValueChanged={onChangeValueHandle("systemId")}
                  errorMessage={errorMessages(
                    pageState.fieldPath.systemId,
                    isModalPage(),
                  )}
                  constraint="Size(value, {min: 0, max: 256})"
                />
                <FrVrfSingleLineInputField
                  labelContent="Sub System ID"
                  content={searchResult.subSystemId}
                  onInputValueChanged={onChangeValueHandle("subSystemId")}
                  errorMessage={errorMessages(
                    pageState.fieldPath.subSystemId,
                    isModalPage(),
                  )}
                  constraint="Size(value, {min: 0, max: 256})"
                />
                <FrVrfCheckboxField
                  labelContent="Not System Usage"
                  checked={isChecked(searchResult.notSystemUsg)}
                  onCheckedChanged={onChnageCheckHandle("notSystemUsg")}
                  errorMessage={errorMessages(
                    pageState.fieldPath.notSystemUsg,
                    isModalPage(),
                  )}
                />
              </FrVrfFormPanel>
              <FrVrfFormPanel>
                <FrVrfDropdownField
                  autocomplete={false}
                  labelContent="Component Allocation Type"
                  selectedValue={searchResult.alctnTypeCd}
                  includeDefaultValue={false}
                  items={dataList.cpntAlctnType}
                  onValueChanged={onChangeValueHandle("alctnTypeCd")}
                  errorMessage={errorMessages(
                    pageState.fieldPath.alctnTypeCd,
                    isModalPage(),
                  )}
                />
                <FrVrfDropdownField
                  autocomplete={false}
                  labelContent="Target Cloud Services"
                  selectedValue={searchResult.clodSrvcTypeCd}
                  includeDefaultValue={false}
                  items={dataList.clodSrvc}
                  onValueChanged={onChangeValueHandle("clodSrvcTypeCd")}
                  errorMessage={errorMessages(
                    pageState.fieldPath.clodSrvcTypeCd,
                  )}
                />
                <FrVrfDropdownField
                  autocomplete={false}
                  labelContent="Target Cloud Region"
                  selectedValue={searchResult.rgnTypeCd}
                  includeDefaultValue={false}
                  items={dataList.clodSrvcRgn}
                  onValueChanged={onChangeValueHandle("rgnTypeCd")}
                  errorMessage={errorMessages(
                    pageState.fieldPath.rgnTypeCd,
                    isModalPage(),
                  )}
                />
                <FrVrfSingleLineInputField
                  labelContent="Target Cloud Account"
                  content={searchResult.acntName}
                  onInputValueChanged={onChangeValueHandle("acntName")}
                  errorMessage={errorMessages(
                    pageState.fieldPath.acntName,
                    isModalPage(),
                  )}
                  constraint="Size(value, {min: 0, max: 256})"
                />
                <FrVrfPanel panelTitle="Pic Address">
                  <FrVrfButtonPanel>
                    <FrVrfButton
                      content="Add Row"
                      size="medium"
                      theme="secondary"
                      slot="left"
                      onClicked={onAddRowClicked}
                      authActId="component_addition.button.add_row"
                    />
                  </FrVrfButtonPanel>
                  <br />
                  <FrVrfSpreadsheet
                    data={spreadsheetInitialData}
                    templateUrl={TEMPLATE_FILE_URL}
                    onInitialized={initSpreadsheetState}
                    onCellSelected={onCellSelected}
                    onCellClicked={onDeleteIconClicked}
                    onCellEdited={onEditSpreadsheet}
                    onCellBulkEdited={onCellBulkEdited}
                    maxHeight={320}
                  />
                </FrVrfPanel>
              </FrVrfFormPanel>
            </FrVrfFormPanel>
          </FrVrfPanel>
        </FrVrfAccordion>
      )}
      <br />
      {/* Component */}
      <FrVrfAccordion summary="Component">
        <FrVrfPanel>
          <FrVrfFormPanel columns={2}>
            <FrVrfFormPanel>
              {params.mode === ComponentAdditionPageMode.ADD_COMPONENT && (
                <>
                  <FrVrfDropdownField
                    autocomplete={false}
                    labelContent="DIP by Region"
                    selectedValue={searchResult.dipByRgn}
                    includeDefaultValue={false}
                    items={dataList.dipByRgnList}
                    onValueChanged={onChangeValueHandle("dipByRgn")}
                    required
                    errorMessage={errorMessages(
                      pageState.fieldPath.dipByRgn,
                      isModalPage(),
                    )}
                  />
                  <FrVrfDropdownField
                    labelContent="Root Component ID"
                    selectedValue={searchResult.rootCpntId}
                    defaultValue={params.dipByRgn}
                    includeDefaultValue={false}
                    items={dataList.rootComponent}
                    onValueChanged={onChangeValueHandle("rootCpntId")}
                    required
                    errorMessage={errorMessages(
                      pageState.fieldPath.rootCpntId,
                      isModalPage(),
                    )}
                    disabled={!searchResult.dipByRgn}
                  />
                </>
              )}

              <FrVrfSingleLineInputField
                labelContent="Component ID"
                content={searchResult.cpntId}
                onInputValueChanged={onChangeValueHandle("cpntId")}
                required
                errorMessage={errorMessages(
                  pageState.fieldPath.cpntId,
                  isModalPage(),
                )}
                constraint="[Size(value, {min: 0, max: 64}), IsNotEmpty(value), Pattern(value,{regex:'^[0-9A-Z_]+$',name:'Component ID'})]"
              />
              <FrVrfSingleLineInputField
                labelContent="Component Name"
                content={searchResult.cpntName}
                onInputValueChanged={onChangeValueHandle("cpntName")}
                required
                errorMessage={errorMessages(
                  pageState.fieldPath.cpntName,
                  isModalPage(),
                )}
                constraint="[Size(value, {min: 0, max: 256}), IsNotEmpty(value)]"
              />
              <FrVrfMultipleLineInputField
                tabindex="1"
                labelContent="Description"
                resize="both"
                content={searchResult.cpntDesc}
                onInputValueChanged={onChangeValueHandle("cpntDesc")}
                errorMessage={errorMessages(
                  pageState.fieldPath.cpntDesc,
                  isModalPage(),
                )}
                constraint="Size(value, {min: 0, max: 2000})"
              />
              <FrVrfCheckboxField
                labelContent="GPG Encryption"
                checked={isChecked(searchResult.gpgEncryptnFlg)}
                onCheckedChanged={onChnageCheckboxHandle("gpgEncryptnFlg")}
                errorMessage={errorMessages(
                  pageState.fieldPath.gpgEncryptnFlg,
                  isModalPage(),
                )}
              />
            </FrVrfFormPanel>
            <FrVrfFormPanel>
              <FrVrfCheckboxField
                labelContent="Use Assumed Role"
                checked={isChecked(searchResult.useAssmdRoleFlg)}
                onCheckedChanged={onChnageCheckboxHandle("useAssmdRoleFlg")}
                errorMessage={errorMessages(
                  pageState.fieldPath.useAssmdRoleFlg,
                )}
              />
              <FrVrfCheckboxField
                labelContent="Use EDH Converter"
                checked={isChecked(searchResult.useEdhCnvtrFlg)}
                onCheckedChanged={onChnageCheckboxHandle("useEdhCnvtrFlg")}
                errorMessage={errorMessages(
                  pageState.fieldPath.useEdhCnvtrFlg,
                  isModalPage(),
                )}
              />
              <FrVrfCheckboxField
                labelContent="HUB Flag"
                checked={isChecked(searchResult.hubFlg)}
                onCheckedChanged={onChnageCheckboxHandle("hubFlg")}
                errorMessage={errorMessages(
                  pageState.fieldPath.hubFlg,
                  isModalPage(),
                )}
              />
              <FrVrfCheckboxesField
                labelContent="EDH Protocol"
                options={dataList.dynamicEdhProtocolList}
                checkList={formEditData.edhPrtclList}
                onCheckboxesChanged={onChnageCheckboxesHandle("edhPrtclList")}
                errorMessage={errorMessages(
                  pageState.fieldPath.edhPrtclList,
                  isModalPage(),
                )}
              />
              <FrVrfCheckboxesField
                labelContent="IF Start Method"
                options={dataList.startMethod}
                checkList={formEditData.ifStrtMthdList}
                onCheckboxesChanged={onChnageCheckboxesHandle("ifStrtMthdList")}
                errorMessage={errorMessages(
                  pageState.fieldPath.ifStrtMthdList,
                  isModalPage(),
                )}
              />
              <FrVrfCheckboxesField
                labelContent="Inbound Notificator"
                options={dataList.inboundNotificator}
                checkList={formEditData.inboundNotificatorList}
                onCheckboxesChanged={onChnageCheckboxesHandle(
                  "inboundNotificatorList",
                )}
                errorMessage={errorMessages(
                  pageState.fieldPath.edhNtfctnTypeList,
                )}
              />
              <FrVrfCheckboxesField
                labelContent="Outbound Notificator"
                options={dataList.outboundNotificator}
                checkList={formEditData.outboundNotificatorList}
                onCheckboxesChanged={onChnageCheckboxesHandle(
                  "outboundNotificatorList",
                )}
                errorMessage={errorMessages(
                  pageState.fieldPath.edhNtfctnTypeList,
                )}
              />
              <FrVrfCheckboxField
                labelContent="EDI Usage"
                checked={isChecked(searchResult.ediUsgFlg)}
                onCheckedChanged={onChnageCheckboxHandle("ediUsgFlg")}
                errorMessage={errorMessages(
                  pageState.fieldPath.ediUsgFlg,
                  isModalPage(),
                )}
              />
            </FrVrfFormPanel>
          </FrVrfFormPanel>
        </FrVrfPanel>
      </FrVrfAccordion>
      <br />
      {/* Footer */}
      <FrVrfFooter>
        <FrVrfButton
          content="Cancel"
          theme="secondary"
          slot="left"
          size="large"
          onClicked={() => {
            if (pageState.isEdited) {
              showConfirmationDialog({
                type: "confirm",
                title: "",
                message: messageForScreen(
                  ApplicationMessageScreen.APP_SCN_00033,
                ),
                onOkButtonClicked: () => {
                  navigate(-1);
                },
              });
            } else {
              navigate(-1);
            }
          }}
        />
        <FrVrfButton
          content="Save"
          theme="primary"
          size="large"
          onClicked={onClickSaveButton}
          disabled={
            !pageState.isEdited ||
            Object.values(pageState.formValidation).some(
              (isValid) => isValid === false,
            )
          }
          authActId="component_addition.button.save"
        />
      </FrVrfFooter>
    </>
  );
};
